<script lang="ts">
  import type { Icon as IconType } from '$lib/icons';
  import Icon from '$components/elements/Icon.svelte';
  import ConditionalWrapper from '$components/utils/ConditionalWrapper.svelte';
  import type { FormStatus } from '$lib/form';
  import Loader from '$components/elements/Loader.svelte';

  export let clazz = '';
  export let color = '#1d1d1b';
  export let textColor = '#fff';
  export let border: string | undefined = undefined;
  export let width = 'fit-content';
  export let icon: IconType | undefined = undefined;
  export let iconOnLeft = false;
  export let iconColor: string | undefined = undefined;
  export let iconSize = 18;
  export let iconViewBox = 24;
  export let submit = false;
  export let download: string | undefined = undefined;
  export let type: 'normal' | 'spacious' = 'normal';
  export let href: string | undefined = undefined;
  export let target: '_blank' | '_self' | '_parent' | '_top' | undefined = undefined;
  export let slimness: 'slim' | 'super-slim' | false = false;
  export let padding =
    slimness === 'super-slim' ? '0 22px' : slimness === 'slim' ? '0 20px' : '0 30px';
  export let loading = false;
  export let status: FormStatus | undefined = undefined;
  export let onFinished: (() => void) | undefined = undefined;
  const loadingStore = status?.loading;
  const successStore = status?.success;

  let initial = true;
  export let checkmark = false;

  export let disabled = false;

  $: if (loading === true) {
    initial = false;
  }

  if (status && onFinished) {
    status.listen().then(onFinished);
  }
  $: if (!loading && !initial && onFinished) {
    checkmark = true;
    setTimeout(() => {
      checkmark = false;
      onFinished && onFinished();
    }, 1600);
  }
</script>

<ConditionalWrapper tag={href && !loading && !disabled && 'a'} {href} {target} {download}>
  <button
    on:click
    class="button button--{type} {clazz}"
    class:button--slim={slimness === 'slim'}
    class:button--super-slim={slimness === 'super-slim'}
    style="--button-width: {width}; --button-background-color: {color}; --button-border: {border}; --button-text-color: {textColor}; --button-padding: {padding};"
    type={submit ? `submit` : `button`}
    {disabled}
  >
    {#if status === undefined ? loading : $loadingStore}
      <Loader color={textColor} size={32} />
    {:else if status === undefined ? checkmark : $successStore}
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
        ><polyline points="20 6 9 17 4 12" /></svg
      >
    {:else}
      {#if icon && iconOnLeft}
        <Icon {icon} color={iconColor} size={iconSize} viewBox={iconViewBox} />
      {/if}
      <slot />
      {#if icon && !iconOnLeft}
        <Icon {icon} color={iconColor} size={iconSize} viewBox={iconViewBox} />
      {/if}
    {/if}
  </button>
</ConditionalWrapper>

<style lang="scss">
  .button {
    height: 56px;
    line-height: 56px;
    width: var(--button-width);
    text-align: center;
    background-color: var(--button-background-color);
    color: var(--button-text-color);
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    appearance: none;
    -webkit-appearance: none;
    outline: none;
    border: none;
    font-size: 16px;
    transition: 0.1s ease all;
    padding: var(--button-padding);
    border-radius: 28px;
    white-space: nowrap;
    word-wrap: none;
    border: var(--button-border);
    svg {
      stroke-width: 2px;
      stroke-linecap: round;
      stroke-linejoin: round;
      fill: none;
      stroke: var(--button-text-color);
      stroke-dasharray: 100% 100%;
      animation: checkmark 0.4s ease-in-out forwards;
    }
    &--normal {
      gap: 9px;
    }
    &--spacious {
      gap: 29px;
    }
    &--slim {
      height: 48px;
      line-height: 48px;
      border-radius: 24px;
      font-size: 14px;
    }
    &--super-slim {
      height: 43px;
      line-height: 43px;
      border-radius: 21.5px;
      font-size: 14px;
    }
    &--caramel-border {
      border: 1px solid $color-caramel;
      color: $color-caramel;

      background-color: $color-white;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
  @include mobile {
    .button {
      height: 48px;
      border-radius: 24px;
      font-size: 14px;
      &--spacious {
        gap: 18px;
      }
    }
  }

  @keyframes checkmark {
    0% {
      stroke-dashoffset: 100%;
    }
    100% {
      stroke-dashoffset: 200%;
    }
  }
</style>
